import { useMemo } from 'react';

import { monorepoRoutePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Gift, Shipping, Shop } from '@withjoy/joykit/icons';

import { useFeatureValue } from '@shared/core/featureFlags';
import { useInternationalRegistryExperience } from '@apps/registry/common/hooks/useInternationalRegistryExperience';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { PageInfo } from './useProductRows';

export const useRegistryProductRow = (_eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals, pendingRefundOrdersCount?: number): ProductRow => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { registry: registryTranslations } = t2('sections');
  const registryTitlesExperimentEnabled = useFeatureValue('registryDashboardTitlesExperiment').value === 'treatment';
  const { isInternationalRegistry } = useInternationalRegistryExperience();

  const registryProductRow = useMemo<ProductRow>(() => {
    const registryStrings = registryTranslations;
    const isEnabled = !productVerticals.optOutOfRegistry;

    const manageRoutePath = isInternationalRegistry ? monorepoRoutePaths.registryInternationalManage : monorepoRoutePaths.registryManage;
    const trackRoutePath = isInternationalRegistry ? monorepoRoutePaths.registryInternationalTrack : monorepoRoutePaths.registryTrack;

    return {
      title: registryStrings.title,
      isEnabled,
      children: [
        // The lean international registry has no US catalog/shop; its browse entry
        // point is the cash-fund Add Gifts page, which takes the Shop slot so the
        // hamburger menu and command palette expose Add Gifts, Manage, and Track.
        ...(isInternationalRegistry
          ? [
              {
                id: 'registry-international-add-gifts',
                title: tq`Add Gifts`,
                icon: Shop,
                path: routeHelpers.buildPath(monorepoRoutePaths.registryInternationalAddGifts),
                pathIsInJoyWeb: true,
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'addGifts', section: fromDropdown ? 'Registry' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.registryInternationalAddGifts,
              },
            ]
          : [
              {
                id: 'registry-shop',
                title: registryTitlesExperimentEnabled ? registryStrings.children.shop.experimentTitle : registryStrings.children.shop.title,
                icon: Shop,
                path: routeHelpers.buildPath(monorepoRoutePaths.registryShop),
                pathIsInJoyWeb: true,
                badge: 'New',
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'shop', section: fromDropdown ? 'Registry' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.registryShop,
              },
            ]),
        {
          id: 'registry-manage',
          title: registryStrings.children.manage.title,
          icon: Gift,
          path: routeHelpers.buildPath(manageRoutePath),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'manage', section: fromDropdown ? 'Registry' : '', categoryArg }),
          telemetryName: manageRoutePath,
        },
        {
          id: 'registry-track',
          title: registryStrings.children.track.title,
          icon: Shipping,
          path: routeHelpers.buildPath(trackRoutePath),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'track', section: fromDropdown ? 'Registry' : '', categoryArg }),
          telemetryName: trackRoutePath,
          notificationPill: pendingRefundOrdersCount ? String(pendingRefundOrdersCount) : undefined,
        },
      ],
    };
  }, [registryTranslations, routeHelpers, telemetry, productVerticals, pendingRefundOrdersCount, registryTitlesExperimentEnabled, isInternationalRegistry, tq]);

  return registryProductRow;
};
